import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from "react-intl";

import {styled} from "@mui/material/styles";

import Badge from "components/Badge";
import CustomTooltip from "components/CustomTooltip";
import ListingCtrBadge from "./ListingCtrBadge";
import ScoringInfoDrawer from "components/Product/ScoringInfoDrawer";
import completenessColor from "./completenessColor";
import {greyishBlue400} from "assets/jss/main";
import {getScoreTheme} from "views/marketplace/retailer/product/utils/ScoreColorCalculator";

ListingScoringCell.propTypes = {
    // quality score /100, hidden entirely when absent
    score: PropTypes.number,
    // full scoring breakdown (ES field `scoring`) opened in a drawer from the
    // score badge; without it the badge is not clickable and carries no info icon
    scoring: PropTypes.object,
    // date the scoring snapshot was taken (ES field `scoringDate`, RFC3339)
    scoringDate: PropTypes.string,
    // "Complet à X%" pill, hidden when null: share of passing product validation checks
    // (Product::validationScore, /100). Green at 100%, orange below, red at or under 20%.
    completeness: PropTypes.number,
    // "CTR : X%" pill, hidden when null
    ctr: PropTypes.number,
};

const Container = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '12px',
    // 104px, fixed (DS "Quality Score Container", w-104px shrink-0).
    width: '104px'
});

const ScoreBlock = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '4px'
});

// the tooltip needs a ref-forwarding anchor, which Badge is not
const BadgeTooltipAnchor = styled('span')({
    display: 'inline-flex'
});

const Label = styled('span')({
    color: greyishBlue400,
    fontSize: '12px',
    whiteSpace: 'nowrap'
});

// the drawer lives in a MUI portal, but React events still bubble through the
// virtual tree up to the row's navigation onClick; display none because the
// wrapper stays empty in the DOM and must not add a flex gap to the container
const DrawerClickBoundary = styled('div')({
    display: 'none'
});

function ListingScoringCell({score, scoring, scoringDate, completeness, ctr}) {
    const hasScore = null !== score && undefined !== score;
    const hasScoring = null !== scoring && undefined !== scoring;
    const hasCompleteness = null !== completeness && undefined !== completeness;
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    // the drawer keeps its content in the DOM even when closed (SwipeableDrawer),
    // so it is only mounted once actually opened: the table renders dozens of rows
    const [isDrawerMounted, setIsDrawerMounted] = useState(false);

    const openDrawer = (event) => {
        // the row itself navigates to the product on click
        event.stopPropagation();
        setIsDrawerMounted(true);
        setIsDrawerOpen(true);
    };

    return (
        <Container>
            {/* No quality score means nothing to show: the badge and its label go away
                together rather than displaying an N/A placeholder. */}
            {hasScore && (
                <ScoreBlock>
                    {/* an empty title disables the tooltip: no breakdown, no click, no hint */}
                    <CustomTooltip title={hasScoring ? <FormattedMessage id={"retailer.product.list.scoring.clickHint"}/> : ''}>
                        <BadgeTooltipAnchor>
                            <Badge
                                rounded
                                color={getScoreTheme(score).theme}
                                border
                                icon={hasScoring ? 'end' : false}
                                iconSize={12}
                                onClick={hasScoring ? openDrawer : null}
                            >
                                {`${score}`}
                            </Badge>
                        </BadgeTooltipAnchor>
                    </CustomTooltip>
                    <Label>
                        <FormattedMessage id={"retailer.product.list.scoring.label"}/>
                    </Label>
                </ScoreBlock>
            )}
            {isDrawerMounted && (
                <DrawerClickBoundary onClick={(event) => event.stopPropagation()}>
                    <ScoringInfoDrawer
                        open={isDrawerOpen}
                        onClose={() => setIsDrawerOpen(false)}
                        scoring={scoring}
                        scoringDate={scoringDate}
                    />
                </DrawerClickBoundary>
            )}
            {/* Shown on its own terms: it does not disappear with the quality score above,
                but an absent value shows nothing rather than a placeholder. */}
            {hasCompleteness && (
                <Badge rounded color={completenessColor(completeness)} border>
                    <FormattedMessage id={"retailer.product.list.scoring.completeness"} values={{value: completeness}}/>
                </Badge>
            )}
            <ListingCtrBadge ctr={ctr}/>
        </Container>
    );
}

export default ListingScoringCell;
